import csiCard from "csi/card/_base";
import Spec from "csi/core/Spec";
import SpecBuilder from "csi/core/SpecBuilder";
import { flattenAttributeKeys } from "csi/middleware/attributes/attributeKeysService";
import { loadCustomViewAttributeKeys } from "csi/service/viewService";
import { difference, keyBy, keys } from "lodash";
import { C8Node, C8NodeType } from "src/c8-platform/type/node";
import { cnl } from "src/c8-platform/type/value";
import { resolveNodes } from "./nodeService";

type ClassType = string;
type FilterAttributeMap = Record<ClassType, Spec[]>;
type UrlTypeMap = Record<cnl, ClassType>;

const FILTER_ATTRIBUTE_MAP: FilterAttributeMap = {};

/** custom view container URL for custom views used to configure Filter Attributes */
export const FILTER_ATTRIBUTES_CONTAINER_URL = "centric://PREFERENCE/FilterAttributes";

/**
 * Return an array of Specs for a given custom view or type.
 * If there is no custom view node, use Attribute Card from type instead.
 * @param {C8Node} customView - custom view node
 * @param {string} type - type of filter attributes to get
 */
export function getFilterAttributesFromCustomView(type: C8NodeType, customView?: C8Node): Spec[] {
	const filterSpecs = [];
	
	if (customView) {
		const specBuilder = new SpecBuilder();
		const { attributeKeys } = loadCustomViewAttributeKeys(customView);
		
		if (attributeKeys) {
			flattenAttributeKeys(attributeKeys, true).forEach((key) => {
				filterSpecs.push(specBuilder.getSpecForKey(key));
			});
		}
	} else {
		const cardSpecs = csiCard.getAttributeCardSpecs(type);
		cardSpecs.attributeSpecs.forEach((spec) => {
			filterSpecs.push(spec);
		});
	}

	return filterSpecs;
}

/** return specs for given node type */
export function getFilterSpecsForType(type: string): Spec[] {
	return FILTER_ATTRIBUTE_MAP[type] || [];
}

/** return URL for custom view holding keys for Filter Attributes of given type */
export function getFilterAttributesUrlForType(type: C8NodeType): string {
	return `${FILTER_ATTRIBUTES_CONTAINER_URL}/${type}`;
}

/** populate the filter spec map with types */
export async function populateFilterSpecsForTypes(types: string[]): Promise<void> {
	const urlTypeMap: UrlTypeMap = {};
	
	// only get unique types that don't already exist in the map
	const uniqNewTypes = new Set((difference(types, keys(FILTER_ATTRIBUTE_MAP))));

	uniqNewTypes.forEach((type) => {
		const cnl = getFilterAttributesUrlForType(type);
		urlTypeMap[cnl] = type;
	});

	const nodes = keyBy(await resolveNodes(keys(urlTypeMap)), "$URL");

	for (const cnl in urlTypeMap) {
		const type = urlTypeMap[cnl];
		const filterSpecs = getFilterAttributesFromCustomView(type, nodes[cnl]);
		FILTER_ATTRIBUTE_MAP[type] = filterSpecs;
	}
}
